// ============================================================================
// FloatingWorkOrderPanel.js — Persistent panel that stays open across pages
// Minimizes to a small tab, expands to full detail view
// Visual status stepper with forward + backward movement
// ============================================================================
import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/axiosClient';
import { useWorkOrderPanel } from './contexts/WorkOrderPanelContext';


const STATUS_CONFIG = {
  received:      { label: 'Received',      color: '#6366f1', bg: '#eef2ff', icon: '📥', step: 0 },
  diagnosed:     { label: 'Diagnosed',     color: '#f59e0b', bg: '#fffbeb', icon: '🔍', step: 1 },
  approved:      { label: 'Approved',      color: '#10b981', bg: '#ecfdf5', icon: '✅', step: 2 },
  in_progress:   { label: 'In Progress',   color: '#3b82f6', bg: '#eff6ff', icon: '🔧', step: 3 },
  quality_check: { label: 'QC',            color: '#8b5cf6', bg: '#f5f3ff', icon: '🔬', step: 4 },
  ready:         { label: 'Ready',         color: '#059669', bg: '#ecfdf5', icon: '🏁', step: 5 },
  picked_up:     { label: 'Picked Up',     color: '#64748b', bg: '#f8fafc', icon: '🏍️', step: 6 },
  closed:        { label: 'Closed',        color: '#94a3b8', bg: '#f1f5f9', icon: '📁', step: 7 },
  cancelled:     { label: 'Cancelled',     color: '#ef4444', bg: '#fef2f2', icon: '❌', step: -1 },
};

const STATUS_FLOW = ['received','diagnosed','approved','in_progress','quality_check','ready','picked_up','closed'];
const ALL_STATUSES = ['received','diagnosed','approved','in_progress','quality_check','ready','picked_up','closed','cancelled'];

// ════════════════════════════════════════════════════════════════
// STATUS STEPPER COMPONENT
// ════════════════════════════════════════════════════════════════
function StatusStepper({ currentStatus, onStatusChange, woId }) {
  var [confirmTarget, setConfirmTarget] = useState(null);
  var currentIdx = STATUS_FLOW.indexOf(currentStatus);
  var isCancelled = currentStatus === 'cancelled';

  var handleClick = function(targetStatus) {
    var targetIdx = STATUS_FLOW.indexOf(targetStatus);
    // Moving backward — confirm first
    if (targetIdx < currentIdx && !isCancelled) {
      setConfirmTarget(targetStatus);
      return;
    }
    onStatusChange(woId, targetStatus);
  };

  var confirmMove = function() {
    if (confirmTarget) {
      onStatusChange(woId, confirmTarget);
      setConfirmTarget(null);
    }
  };

  return (
    <div style={{ padding: '4px 0 8px' }}>
      {/* Confirmation banner */}
      {confirmTarget && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 10, padding: '10px 14px',
          marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
            ← Move back to <strong>{STATUS_CONFIG[confirmTarget]?.label}</strong>?
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={confirmMove} style={{
              padding: '5px 14px', borderRadius: 6, border: 'none', background: '#f59e0b',
              color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>Confirm</button>
            <button onClick={function() { setConfirmTarget(null); }} style={{
              padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff',
              color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Stepper track */}
      <div style={{ position: 'relative', padding: '0 4px' }}>
        {/* Connection line */}
        <div style={{
          position: 'absolute', top: 16, left: 24, right: 24, height: 3,
          background: '#e2e8f0', borderRadius: 2, zIndex: 0,
        }} />
        {/* Progress fill */}
        {currentIdx >= 0 && (
          <div style={{
            position: 'absolute', top: 16, left: 24, height: 3, borderRadius: 2, zIndex: 1,
            width: currentIdx === 0 ? 0 : `calc(${(currentIdx / (STATUS_FLOW.length - 1)) * 100}% - 48px * ${(STATUS_FLOW.length - 1 - currentIdx) / (STATUS_FLOW.length - 1)})`,
            background: `linear-gradient(90deg, ${STATUS_CONFIG[STATUS_FLOW[0]]?.color}, ${STATUS_CONFIG[currentStatus]?.color || '#94a3b8'})`,
            transition: 'width 0.4s ease',
          }} />
        )}

        {/* Status nodes */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 2,
        }}>
          {STATUS_FLOW.map(function(status, idx) {
            var cfg = STATUS_CONFIG[status];
            var isCurrent = status === currentStatus;
            var isPast = !isCancelled && currentIdx >= 0 && idx < currentIdx;
            var isFuture = !isCancelled && currentIdx >= 0 && idx > currentIdx;

            return (
              <div key={status} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                cursor: isCurrent ? 'default' : 'pointer', flex: 1, minWidth: 0,
              }}
                onClick={function() { if (!isCurrent) handleClick(status); }}
              >
                {/* Node circle */}
                <div style={{
                  width: isCurrent ? 34 : 28, height: isCurrent ? 34 : 28,
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isCurrent ? 16 : 12,
                  background: isCurrent ? cfg.color : isPast ? cfg.color : '#f1f5f9',
                  color: isCurrent || isPast ? '#fff' : '#94a3b8',
                  border: isCurrent ? '3px solid ' + cfg.color + '40' : isPast ? '2px solid ' + cfg.color : '2px solid #e2e8f0',
                  boxShadow: isCurrent ? '0 0 0 4px ' + cfg.color + '20, 0 4px 12px ' + cfg.color + '30' : 'none',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                }}>
                  {isCurrent ? cfg.icon : isPast ? '✓' : cfg.icon}
                </div>
                {/* Label */}
                <span style={{
                  fontSize: 9, fontWeight: isCurrent ? 800 : 600,
                  color: isCurrent ? cfg.color : isPast ? '#475569' : '#94a3b8',
                  textAlign: 'center', lineHeight: 1.2, letterSpacing: '0.2px',
                  transition: 'color 0.3s',
                  maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick action row */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'center' }}>
        {/* Back button */}
        {currentIdx > 0 && (
          <button onClick={function() { handleClick(STATUS_FLOW[currentIdx - 1]); }}
            style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              transition: 'all 0.2s',
            }}>
            ← Back
          </button>
        )}
        {/* Forward button */}
        {currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 && (
          <button onClick={function() { onStatusChange(woId, STATUS_FLOW[currentIdx + 1]); }}
            style={{
              padding: '6px 18px', borderRadius: 8, border: 'none',
              background: STATUS_CONFIG[STATUS_FLOW[currentIdx + 1]]?.color,
              color: '#fff', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', transition: 'all 0.2s',
            }}>
            → {STATUS_CONFIG[STATUS_FLOW[currentIdx + 1]]?.label}
          </button>
        )}
        {/* Cancel / Reopen */}
        {!isCancelled && currentIdx >= 0 && currentIdx < 6 && (
          <button onClick={function() { if (window.confirm('Cancel this work order?')) onStatusChange(woId, 'cancelled'); }}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid #fecaca',
              background: '#fef2f2', color: '#ef4444', fontSize: 11, fontWeight: 600,
              cursor: 'pointer',
            }}>
            ✕ Cancel
          </button>
        )}
        {isCancelled && (
          <button onClick={function() { onStatusChange(woId, 'received'); }}
            style={{
              padding: '6px 18px', borderRadius: 8, border: 'none',
              background: '#6366f1', color: '#fff', fontSize: 12, fontWeight: 700,
              cursor: 'pointer',
            }}>
            ↺ Reopen
          </button>
        )}
      </div>
    </div>
  );
}

// Multi-panel wrapper — renders all open panels
export default function FloatingWorkOrderPanel() {
  const { panels, activeId, focusPanel } = useWorkOrderPanel();
  if (panels.length === 0) return null;
  return <>
    {panels.map((panel, idx) => (
      <SinglePanel key={panel.wo?.id || panel.id} panel={panel} index={idx} total={panels.length} />
    ))}
  </>;
}

function SinglePanel({ panel, index, total }) {
  const {
    closePanel, toggleMinimize, focusPanel, activeId,
    refreshPanel, updateStatus, addLineItem, deleteLineItem, deleteWorkOrder,
    headers,
  } = useWorkOrderPanel();

  const wo = panel.wo;
  const loading = panel.loading;
  const minimized = panel.minimized;

  const [activeTab, setActiveTab] = useState('details');
  const [newItem, setNewItem] = useState({ type: 'labor', name: '', quantity: '', unit_price: '' });
  const [showAddItem, setShowAddItem] = useState(false);

  // Inspection state
  const [inspection, setInspection] = useState(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  // Time tracking state
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeSummary, setTimeSummary] = useState(null);
  // Photos state
  const [photos, setPhotos] = useState([]);
  // Parts state
  const [products, setProducts] = useState([]);
  const [partSearch, setPartSearch] = useState('');

  // Dragging state
  const [position, setPosition] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });



  // Reset tab data when WO changes
  useEffect(() => {
    setActiveTab('details');
    setInspection(null);
    setTimeEntries([]);
    setPhotos([]);
    setProducts([]);
    setPartSearch('');
  }, [wo?.id]);

  // Load tab data
  useEffect(() => {
    if (!wo) return;
    if (activeTab === 'inspection') loadInspection();
    if (activeTab === 'time') loadTimeEntries();
    if (activeTab === 'photos') loadPhotos();
    if (activeTab === 'parts') loadProducts();
  }, [activeTab, wo?.id]);

  // Drag handlers
  const onDragStart = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
    setDragging(true);
    const rect = dragRef.current.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      setPosition({ x: e.clientX - offsetRef.current.x, y: e.clientY - offsetRef.current.y });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // === TAB DATA LOADERS ===
  const loadInspection = async () => {
    setInspectionLoading(true);
    try {
      const res = await axios.get(`/api/inspections?work_order_id=${wo.id}`);
      if (res.data.inspections?.length > 0) setInspection(res.data.inspections[0]);
    } catch (e) {}
    setInspectionLoading(false);
  };
  const createInspection = async () => {
    setInspectionLoading(true);
    try {
      const res = await axios.post(`/api/inspections`, { work_order_id: wo.id, vehicle_id: wo.vehicle_id || null });
      setInspection(res.data.inspection);
    } catch (e) { alert('Failed to create inspection'); }
    setInspectionLoading(false);
  };
  const updateInspectionItem = async (itemId, status, notes) => {
    if (!inspection) return;
    const updated = inspection.inspection_items.map(i => i.id === itemId ? { ...i, status, notes: notes !== undefined ? notes : i.notes } : i);
    setInspection({ ...inspection, inspection_items: updated });
    try { await axios.put(`/api/inspections/${inspection.id}/items`, { items: [{ id: itemId, status, notes: notes !== undefined ? notes : '' }] }); } catch (e) {}
  };
  const completeInspection = async () => {
    try { const res = await axios.put(`/api/inspections/${inspection.id}/complete`, {}); setInspection(res.data.inspection); } catch (e) {}
  };
  const loadTimeEntries = async () => {
    try { const res = await axios.get(`/api/time-tracking/summary/${wo.id}`); setTimeEntries(res.data.entries || []); setTimeSummary(res.data.summary); } catch (e) {}
  };
  const clockIn = async (staffId) => {
    try { await axios.post(`/api/time-tracking/clock-in`, { work_order_id: wo.id, staff_id: staffId }); loadTimeEntries(); } catch (e) { alert(e.response?.data?.error || 'Clock-in failed'); }
  };
  const clockOut = async (entryId) => {
    try { await axios.post(`/api/time-tracking/clock-out/${entryId}`, {}); loadTimeEntries(); } catch (e) {}
  };
  const loadPhotos = async () => {
    try { const res = await axios.get(`/api/work-orders/${wo.id}/photos`); setPhotos(res.data.photos || []); } catch (e) {}
  };
  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try { const res = await axios.post(`/api/work-orders/${wo.id}/photos`, { photo_data: ev.target.result, photo_type: 'check_in', caption: '' }); setPhotos(prev => [...prev, res.data.photo]); } catch (err) { alert('Upload failed'); }
    };
    reader.readAsDataURL(file);
  };
  const deletePhoto = async (photoId) => {
    try { await axios.delete(`/api/work-orders/${wo.id}/photos/${photoId}`); setPhotos(prev => prev.filter(p => p.id !== photoId)); } catch (e) {}
  };
  const loadProducts = async () => {
    try { const res = await axios.get(`/api/inventory`); setProducts(res.data.products || []); } catch (e) {}
  };
  const addPartFromInventory = async (productId) => {
    try {
      const res = await axios.post(`/api/work-orders/${wo.id}/add-part`, { product_id: productId, quantity: 1 });
      if (res.data.low_stock) alert(`⚠️ Low stock: ${res.data.new_stock} remaining`);
      refreshPanel(wo.id);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // Convert to Invoice
  const convertToInvoice = async () => {
    if (!window.confirm('Create an invoice from this work order? All line items will be copied.')) return;
    try {
      const items = (wo.items || []).map(i => ({
        type: i.type, name: i.name || i.description,
        quantity: i.quantity || 1, unit_price: i.unit_price || 0,
      }));
      const payload = {
        contact_id: wo.contact_id,
        vehicle_id: wo.vehicle_id,
        work_order_id: wo.id,
        tax_rate: 0,
        discount_amount: 0,
        due_date: new Date(Date.now() + 30*86400000).toISOString().split('T')[0],
        notes: `Invoice for WO ${wo.order_number}: ${wo.title || ''}`,
        items,
      };
      await axios.post(`/api/invoices`, payload);
      alert('✅ Invoice created! Go to Invoices to view it.');
    } catch (e) { alert(e.response?.data?.error || 'Failed to create invoice'); }
  };

  // Print
  const printWO = () => {
    const items = wo.items || [];
    const laborItems = items.filter(i => i.type === 'labor');
    const partItems = items.filter(i => i.type === 'part');
    const feeItems = items.filter(i => i.type === 'fee');
    const itemRows = (list, label) => list.length === 0 ? '' : `
      <tr><td colspan="4" style="padding:10px 0 4px;font-weight:700;font-size:13px;color:#374151;border-bottom:1px solid #e5e7eb;">${label}</td></tr>
      ${list.map(i => `<tr><td style="padding:6px 0">${i.name}</td><td style="text-align:center">${i.quantity}</td><td style="text-align:right">$${parseFloat(i.unit_price||0).toFixed(2)}</td><td style="text-align:right;font-weight:600">$${parseFloat(i.total||0).toFixed(2)}</td></tr>`).join('')}`;
    const html = `<!DOCTYPE html><html><head><title>Work Order ${wo.order_number}</title></head><body style="font-family:-apple-system,sans-serif;max-width:800px;margin:0 auto;padding:40px">
      <div style="text-align:center;margin-bottom:30px"><h1 style="margin:0;font-size:24px">WORK ORDER</h1><div style="color:#64748b">${wo.order_number}</div></div>
      <table style="width:100%;border-collapse:collapse"><tr><td style="vertical-align:top;width:50%"><strong>Customer:</strong><br>${wo.contacts?.first_name||''} ${wo.contacts?.last_name||''}<br>${wo.contacts?.phone||''}<br>${wo.contacts?.email||''}</td>
      <td style="vertical-align:top;text-align:right"><strong>Vehicle:</strong><br>${wo.vehicles?wo.vehicles.year+' '+wo.vehicles.make+' '+wo.vehicles.model:''}<br>${wo.vehicles?.vin||''}</td></tr></table>
      <hr style="margin:20px 0"><table style="width:100%;border-collapse:collapse"><tr style="background:#f8fafc"><th style="text-align:left;padding:8px">Item</th><th style="text-align:center;padding:8px">Qty</th><th style="text-align:right;padding:8px">Price</th><th style="text-align:right;padding:8px">Total</th></tr>
      ${itemRows(laborItems,'Labor')}${itemRows(partItems,'Parts')}${itemRows(feeItems,'Fees')}</table>
      <div style="text-align:right;margin-top:20px;font-size:18px"><strong>Total: $${parseFloat(wo.total||0).toFixed(2)}</strong></div>
      <div style="margin-top:60px;display:flex;justify-content:space-between"><div style="border-top:1px solid #000;width:200px;padding-top:8px;text-align:center">Customer Signature</div><div style="border-top:1px solid #000;width:200px;padding-top:8px;text-align:center">Date</div></div></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  // Handle add line item from form
  const handleAddItem = () => {
    if (!newItem.name) return;
    const qty = parseFloat(newItem.quantity) || 1;
    const price = parseFloat(newItem.unit_price) || 0;
    addLineItem(wo.id, { type: newItem.type, name: newItem.name, quantity: qty, unit_price: price, total: qty * price });
    setNewItem({ type: 'labor', name: '', quantity: '', unit_price: '' });
    setShowAddItem(false);
  };

  if (!wo && !loading) return null;

  // MINIMIZED TAB
  if (minimized && wo) {
    const sc = STATUS_CONFIG[wo.status] || {};
    return (
      <div style={{...S.minimizedTab, bottom: 20 + index * 52}} onClick={() => { toggleMinimize(wo.id); focusPanel(wo.id); }}>
        <span style={{...S.miniBadge, background: sc.bg, color: sc.color}}>{sc.icon}</span>
        <span style={S.miniTitle}>{wo.order_number}</span>
        <span style={S.miniSub}>{wo.title || 'Work Order'}</span>
        <button onClick={(e) => { e.stopPropagation(); closePanel(wo.id); }} style={S.miniClose}>✕</button>
      </div>
    );
  }

  if (loading) return (
    <div style={S.panel} ref={dragRef}>
      <div style={S.panelHeader}><div style={{color:'#94a3b8'}}>Loading work order...</div></div>
    </div>
  );

  const sc = STATUS_CONFIG[wo.status] || {};

  return (
    <div ref={dragRef} onClick={() => focusPanel(wo.id)} style={{
      ...S.panel,
      right: position.x !== null ? 'auto' : 16 + (index * 30),
      zIndex: 9999 - (total - 1 - index),
      ...(position.x !== null ? { right: 'auto', left: position.x, top: position.y } : {}),
    }}>
      {/* DRAG HANDLE + HEADER */}
      <div style={S.panelHeader} onMouseDown={onDragStart}>
        <div style={S.dragHandle}>
          <div style={S.dragDots}>⋮⋮</div>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
            <span style={{fontSize:13,fontWeight:800,color:'#3b82f6'}}>{wo.order_number}</span>
            <span style={{...S.badge, background:sc.bg, color:sc.color}}>{sc.icon} {sc.label}</span>
            {wo.total > 0 && <span style={{fontSize:12,fontWeight:700,color:'#059669',marginLeft:'auto'}}>${parseFloat(wo.total).toFixed(2)}</span>}
          </div>
          <div style={{fontSize:14,fontWeight:700,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{wo.title || 'Untitled'}</div>
        </div>
        <div style={{display:'flex',gap:4}}>
          <button onClick={() => toggleMinimize(wo.id)} style={S.headerBtn} title="Minimize">▬</button>
          <button onClick={() => closePanel(wo.id)} style={{...S.headerBtn, color:'#ef4444'}} title="Close">✕</button>
        </div>
      </div>

      {/* STATUS STEPPER — always visible above tabs */}
      <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid #e5e7eb', background: '#fafbfc' }}>
        <StatusStepper currentStatus={wo.status} onStatusChange={updateStatus} woId={wo.id} />
      </div>

      {/* TAB BAR */}
      <div style={S.tabBar}>
        {[
          {id:'details', label:'📋 Details'},
          {id:'inspection', label:'🔍 Inspect'},
          {id:'photos', label:'📷 Photos'},
          {id:'time', label:'⏱️ Time'},
          {id:'parts', label:'⚙️ Parts'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            ...S.tab,
            color: activeTab===tab.id ? '#3b82f6' : '#64748b',
            borderBottom: activeTab===tab.id ? '2px solid #3b82f6' : '2px solid transparent',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT */}
      <div style={S.panelBody}>

        {/* === DETAILS === */}
        {activeTab === 'details' && <>
          <Section title="Customer & Vehicle">
            {wo.contacts && <Row label="Customer" value={`${wo.contacts.first_name} ${wo.contacts.last_name}`} />}
            {wo.contacts?.phone && <Row label="Phone" value={wo.contacts.phone} />}
            {wo.vehicles && <Row label="Vehicle" value={`${wo.vehicles.year} ${wo.vehicles.make} ${wo.vehicles.model}`} />}
            {wo.vehicles?.vin && <Row label="VIN" value={wo.vehicles.vin} />}
          </Section>

          {wo.notes && <Section title="Notes"><div style={{fontSize:13,color:'#475569',lineHeight:1.5}}>{wo.notes}</div></Section>}

          <Section title={`Line Items (${(wo.items||[]).length})`}>
            {(wo.items||[]).map(item => (
              <div key={item.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #f8fafc'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:500}}>{item.name}</div>
                  <div style={{fontSize:11,color:'#94a3b8'}}>{item.type} · qty {item.quantity}</div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:13,fontWeight:700}}>${parseFloat(item.total||0).toFixed(2)}</span>
                  <button onClick={() => deleteLineItem(wo.id, item.id)} style={{color:'#ef4444',background:'none',border:'none',cursor:'pointer',fontSize:14}}>✕</button>
                </div>
              </div>
            ))}
            {!showAddItem ? (
              <button onClick={() => setShowAddItem(true)} style={{marginTop:8,padding:'6px 14px',background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600,color:'#3b82f6'}}>+ Add Item</button>
            ) : (
              <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}>
                <div style={{display:'flex',gap:6}}>
                  <select value={newItem.type} onChange={e=>setNewItem({...newItem,type:e.target.value})} style={S.miniInput}>
                    <option value="labor">Labor</option><option value="part">Part</option><option value="fee">Fee</option>
                  </select>
                  <input placeholder="Name" value={newItem.name} onChange={e=>setNewItem({...newItem,name:e.target.value})} style={{...S.miniInput,flex:1}} />
                </div>
                <div style={{display:'flex',gap:6}}>
                  <input placeholder="Qty" type="number" value={newItem.quantity} onChange={e=>setNewItem({...newItem,quantity:e.target.value})} style={{...S.miniInput,width:60}} />
                  <input placeholder="Price" type="number" value={newItem.unit_price} onChange={e=>setNewItem({...newItem,unit_price:e.target.value})} style={{...S.miniInput,width:80}} />
                  <button onClick={handleAddItem} style={{padding:'4px 12px',background:'#3b82f6',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>Add</button>
                  <button onClick={()=>setShowAddItem(false)} style={{padding:'4px 8px',background:'#f1f5f9',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}}>✕</button>
                </div>
              </div>
            )}
          </Section>

          <div style={{padding:'12px 0',textAlign:'right'}}>
            <span style={{fontSize:20,fontWeight:800}}>Total: ${parseFloat(wo.total||0).toFixed(2)}</span>
          </div>
        </>}

        {/* === INSPECTION === */}
        {activeTab === 'inspection' && <>
          {inspectionLoading ? (
            <div style={{padding:40,textAlign:'center',color:'#94a3b8'}}>Loading...</div>
          ) : !inspection ? (
            <div style={{padding:40,textAlign:'center'}}>
              <div style={{fontSize:48,marginBottom:12}}>🔍</div>
              <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>No Inspection Started</div>
              <div style={{fontSize:13,color:'#94a3b8',marginBottom:16}}>Start a 32-point motorcycle inspection</div>
              <button onClick={createInspection} style={{padding:'10px 24px',background:'#3b82f6',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:14}}>
                🔍 Start Inspection
              </button>
            </div>
          ) : (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',marginBottom:8}}>
                <div>
                  <span style={{fontSize:12,fontWeight:700,color:inspection.status==='completed'?'#22c55e':'#f59e0b',textTransform:'uppercase'}}>
                    {inspection.status==='completed'?'✅ Completed':'🔄 In Progress'}
                  </span>
                  {(() => { const items=inspection.inspection_items||[]; const checked=items.filter(i=>i.status!=='not_checked').length; return <span style={{fontSize:12,color:'#94a3b8',marginLeft:12}}>{checked}/{items.length}</span>; })()}
                </div>
                {inspection.status !== 'completed' && (
                  <button onClick={completeInspection} style={{padding:'6px 16px',background:'#22c55e',color:'#fff',border:'none',borderRadius:6,fontWeight:600,fontSize:12,cursor:'pointer'}}>✅ Complete</button>
                )}
              </div>
              {(() => {
                const items = inspection.inspection_items || [];
                const categories = [...new Set(items.map(i => i.category))];
                return categories.map(cat => (
                  <div key={cat} style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:6,padding:'4px 0',borderBottom:'1px solid #e5e7eb'}}>{cat}</div>
                    {items.filter(i => i.category === cat).map(item => (
                      <div key={item.id} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0',borderBottom:'1px solid #f8fafc'}}>
                        <div style={{display:'flex',gap:3}}>
                          {['pass','warn','fail'].map(s => (
                            <button key={s} onClick={() => updateInspectionItem(item.id, s)}
                              style={{width:24,height:24,borderRadius:4,border:item.status===s?'2px solid':'1px solid #e2e8f0',fontSize:12,cursor:'pointer',
                                background:item.status===s?(s==='pass'?'#dcfce7':s==='warn'?'#fef3c7':'#fef2f2'):'#fff',
                                borderColor:item.status===s?(s==='pass'?'#22c55e':s==='warn'?'#f59e0b':'#ef4444'):'#e2e8f0'}}>
                              {s==='pass'?'✓':s==='warn'?'⚠':'✕'}
                            </button>
                          ))}
                        </div>
                        <span style={{flex:1,fontSize:12,fontWeight:500,color:item.status==='fail'?'#ef4444':item.status==='warn'?'#92400e':'#374151'}}>{item.check_point}</span>
                        <input placeholder="Note" value={item.notes||''} onChange={e => updateInspectionItem(item.id, item.status, e.target.value)}
                          style={{width:80,padding:'2px 6px',border:'1px solid #e2e8f0',borderRadius:4,fontSize:10,background:'#f8fafc'}} />
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
        </>}

        {/* === PHOTOS === */}
        {activeTab === 'photos' && <>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0'}}>
            <span style={{fontSize:14,fontWeight:700}}>📷 Photos ({photos.length})</span>
            <label style={{padding:'6px 14px',background:'#3b82f6',color:'#fff',borderRadius:6,fontWeight:600,fontSize:12,cursor:'pointer'}}>
              + Add <input type="file" accept="image/*" onChange={addPhoto} style={{display:'none'}} />
            </label>
          </div>
          {photos.length === 0 ? (
            <div style={{padding:40,textAlign:'center',color:'#94a3b8',fontSize:13}}>📷 No photos — snap a check-in photo</div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))',gap:8,marginTop:8}}>
              {photos.map(p => (
                <div key={p.id} style={{position:'relative',borderRadius:8,overflow:'hidden',border:'1px solid #e5e7eb',aspectRatio:'1'}}>
                  <img src={p.photo_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                  <button onClick={() => deletePhoto(p.id)} style={{position:'absolute',top:2,right:2,width:20,height:20,borderRadius:'50%',background:'rgba(0,0,0,0.6)',color:'#fff',border:'none',cursor:'pointer',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                </div>
              ))}
            </div>
          )}
        </>}

        {/* === TIME === */}
        {activeTab === 'time' && <>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0'}}>
            <span style={{fontSize:14,fontWeight:700}}>⏱️ Time Log</span>
            {wo.assigned_staff_id && <button onClick={() => clockIn(wo.assigned_staff_id)} style={{padding:'6px 14px',background:'#22c55e',color:'#fff',borderRadius:6,fontWeight:600,fontSize:12,cursor:'pointer',border:'none'}}>▶ Clock In</button>}
          </div>
          {timeSummary && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
              {[
                { val: timeSummary.total_hours+'h', label: 'Total', color: '#3b82f6' },
                { val: Object.keys(timeSummary.by_staff).length, label: 'Mechanics', color: '#22c55e' },
                { val: timeSummary.active_entries, label: 'Active', color: timeSummary.active_entries>0?'#f59e0b':'#94a3b8' },
              ].map((s,i) => (
                <div key={i} style={{background:'#f8fafc',borderRadius:8,padding:8,textAlign:'center',border:'1px solid #e5e7eb'}}>
                  <div style={{fontSize:18,fontWeight:800,color:s.color}}>{s.val}</div>
                  <div style={{fontSize:10,color:'#94a3b8',fontWeight:600}}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
          {timeEntries.length === 0 ? (
            <div style={{padding:30,textAlign:'center',color:'#94a3b8',fontSize:13}}>⏱️ No time logged yet</div>
          ) : timeEntries.map(entry => (
            <div key={entry.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #f1f5f9'}}>
              <div>
                <div style={{fontSize:12,fontWeight:600}}>{entry.staff?.first_name} {entry.staff?.last_name}</div>
                <div style={{fontSize:10,color:'#94a3b8'}}>{new Date(entry.start_time).toLocaleString()}</div>
              </div>
              {entry.duration_minutes ? (
                <span style={{fontSize:13,fontWeight:700,color:'#3b82f6'}}>{(entry.duration_minutes/60).toFixed(1)}h</span>
              ) : (
                <button onClick={() => clockOut(entry.id)} style={{padding:'4px 10px',background:'#ef4444',color:'#fff',borderRadius:6,fontWeight:600,fontSize:11,cursor:'pointer',border:'none'}}>⏹ Out</button>
              )}
            </div>
          ))}
        </>}

        {/* === PARTS === */}
        {activeTab === 'parts' && <>
          <div style={{padding:'8px 0'}}>
            <span style={{fontSize:14,fontWeight:700}}>⚙️ Add Part from Inventory</span>
            <input placeholder="Search parts..." value={partSearch} onChange={e => setPartSearch(e.target.value)}
              style={{width:'100%',padding:'8px 10px',border:'1px solid #e2e8f0',borderRadius:6,fontSize:12,marginTop:8,boxSizing:'border-box'}} />
            <div style={{maxHeight:250,overflowY:'auto',marginTop:8}}>
              {products.filter(p => !partSearch || p.name.toLowerCase().includes(partSearch.toLowerCase()) || (p.sku||'').toLowerCase().includes(partSearch.toLowerCase()))
                .map(p => (
                  <div key={p.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #f1f5f9'}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:600}}>{p.name}</div>
                      <div style={{fontSize:10,color:'#94a3b8'}}>{p.sku&&`${p.sku} · `}${parseFloat(p.price||0).toFixed(2)}{p.current_stock!==null&&` · ${p.current_stock} stock`}</div>
                    </div>
                    <button onClick={() => addPartFromInventory(p.id)} style={{padding:'3px 10px',background:'#ecfdf5',color:'#059669',border:'1px solid #a7f3d0',borderRadius:6,fontWeight:600,fontSize:10,cursor:'pointer'}}>+ Add</button>
                  </div>
                ))}
              {products.length === 0 && <div style={{padding:20,textAlign:'center',color:'#94a3b8',fontSize:12}}>No parts in inventory</div>}
            </div>
          </div>
        </>}
      </div>

      {/* FOOTER */}
      <div style={S.panelFooter}>
        <button onClick={() => deleteWorkOrder(wo.id)} style={S.deleteBtn}>Delete</button>
        <div style={{display:'flex',gap:6}}>
          <button onClick={convertToInvoice} style={{padding:'6px 14px',background:'#f5f3ff',color:'#8b5cf6',border:'1px solid #c4b5fd',borderRadius:6,fontWeight:600,fontSize:12,cursor:'pointer'}}>💰 Invoice</button>
          <button onClick={printWO} style={S.printBtn}>🖨️ Print</button>
          <button onClick={() => refreshPanel(wo.id)} style={S.refreshBtn}>↻</button>
        </div>
      </div>
    </div>
  );
}

// Helpers
function Section({ title, children }) {
  return (
    <div style={{padding:'12px 0',borderBottom:'1px solid #f1f5f9'}}>
      <div style={{fontSize:12,fontWeight:700,color:'#64748b',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.5px'}}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',fontSize:13}}>
      <span style={{color:'#94a3b8',fontWeight:500}}>{label}</span>
      <span style={{fontWeight:600,color:'#1e293b'}}>{value}</span>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const S = {
  panel: {
    position:'fixed', right:16, top:16, bottom:16, width:440, maxWidth:'92vw',
    background:'#fff', color:'#1e293b', borderRadius:16, boxShadow:'0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
    display:'flex', flexDirection:'column', zIndex:9999, overflow:'hidden',
    transition:'box-shadow 0.2s',
  },
  panelHeader: {
    display:'flex', alignItems:'center', gap:10, padding:'12px 16px',
    borderBottom:'1px solid #e5e7eb', background:'#fff', cursor:'grab',
    borderRadius:'16px 16px 0 0', userSelect:'none',
  },
  dragHandle: { display:'flex', alignItems:'center', cursor:'grab' },
  dragDots: { fontSize:14, color:'#cbd5e1', letterSpacing:'-2px', lineHeight:1 },
  headerBtn: { width:28, height:28, borderRadius:6, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b' },
  badge: { padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700 },
  tabBar: { display:'flex', gap:0, borderBottom:'2px solid #e5e7eb', padding:'0 12px', background:'#fff', flexShrink:0 },
  tab: { padding:'8px 12px', border:'none', background:'none', cursor:'pointer', fontSize:11, fontWeight:600, marginBottom:'-2px', transition:'all 0.15s', whiteSpace:'nowrap' },
  panelBody: { flex:1, padding:'0 16px 16px', overflowY:'auto', overflowX:'hidden' },
  panelFooter: { display:'flex', justifyContent:'space-between', padding:'10px 16px', borderTop:'1px solid #e5e7eb', flexShrink:0 },
  deleteBtn: { padding:'6px 14px', background:'#fef2f2', color:'#ef4444', border:'1px solid #fecaca', borderRadius:6, fontWeight:600, fontSize:12, cursor:'pointer' },
  printBtn: { padding:'6px 14px', background:'#eff6ff', color:'#3b82f6', border:'1px solid #bfdbfe', borderRadius:6, fontWeight:600, fontSize:12, cursor:'pointer' },
  refreshBtn: { padding:'6px 10px', background:'#f8fafc', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:6, fontWeight:600, fontSize:14, cursor:'pointer' },
  miniInput: { padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12 },

  // Minimized tab
  minimizedTab: {
    position:'fixed', bottom:20, right:20, zIndex:9999,
    display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
    background:'#1e293b', color:'#fff', borderRadius:12,
    boxShadow:'0 8px 30px rgba(0,0,0,0.3)', cursor:'pointer',
    transition:'transform 0.2s', userSelect:'none',
  },
  miniBadge: { padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:700 },
  miniTitle: { fontSize:14, fontWeight:800 },
  miniSub: { fontSize:12, color:'#94a3b8', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  miniClose: { marginLeft:8, background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', borderRadius:6, width:24, height:24, cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center' },
};
